import React, { useEffect, useMemo, useState } from 'react';
import { Box, Typography, Button, IconButton, Paper, Chip, Divider, Stack, TextField, CircularProgress } from '@mui/material';
import {
  ArrowBack, Person, CurrencyRupee, Delete, LocalAtm, CreditCard, QrCode2, AccountBalance, AccountBalanceWallet, CardGiftcard, Payment,
  InfoOutlined, GppBad, ErrorOutline, MarkEmailRead, ReceiptLong,
} from '@mui/icons-material';
import { useDispatch, useSelector } from 'react-redux';
import { useGetPaymentModesQuery } from '../../../services/paymentModeApi';
import { usePayGiftVoucherSaleMutation } from '../../../services/giftVoucherApi';
import { useGetReceiptTemplateQuery } from '../../../services/receiptTemplateApi';
import { useGetCustomerQuery } from '../../../services/customerApi';
import { resetGvSale } from '../../../store/giftVoucherSaleSlice';
import { useToast } from '../../common/ToastProvider';
import SuccessOverlay from '../checkout/PaymentSuccessOverlay';
import { mapGiftVoucherSaleToReceipt } from '../receipt/mapGiftVoucherSaleToReceipt';
import { useThermalReceiptPrint } from '../../../hooks/useThermalReceiptPrint';
import {
  inr, inr2, round2, isBlockedForVoucherSale, isCashModeGv, needsReference, isGiftVoucherMode,
} from './gvUtils';

const CASH_LIMIT = 200000; // Income Tax Act s.269ST

function modeIcon(mode, color) {
  const n = `${mode?.name || ''} ${mode?.code || ''}`.toLowerCase();
  const sx = { fontSize: 16, color };
  if (isGiftVoucherMode(mode)) return <CardGiftcard sx={sx} />;
  if (n.includes('cash')) return <LocalAtm sx={sx} />;
  if (n.includes('card')) return <CreditCard sx={sx} />;
  if (n.includes('upi')) return <QrCode2 sx={sx} />;
  if (/bank|neft|rtgs|imps|transfer/.test(n)) return <AccountBalance sx={sx} />;
  if (n.includes('credit') || n.includes('wallet')) return <AccountBalanceWallet sx={sx} />;
  return <Payment sx={sx} />;
}

function StatusPill({ tone, label, value }) {
  const ok = tone === 'ok';
  return (
    <Box sx={{ mt: 1.25, px: 1.5, py: 0.9, borderRadius: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1, border: '1.5px solid', bgcolor: ok ? '#F0FDF4' : '#FEF2F2', borderColor: ok ? '#BBF7D0' : '#FECACA' }}>
      <Typography sx={{ fontSize: '0.7rem', fontWeight: 800, letterSpacing: '0.04em', color: ok ? '#16A34A' : '#DC2626' }}>{label}</Typography>
      <Typography sx={{ fontSize: '0.95rem', fontWeight: 900, color: ok ? '#16A34A' : '#DC2626' }}>{value}</Typography>
    </Box>
  );
}

// ── After paying ────────────────────────────────────────────────────────────
// Same success screen as a normal bill: thermal receipt preview, Print, New
// Sale and Send on WhatsApp. The receipt is a non-tax gift voucher receipt
// numbered from the voucher sale series (mapGiftVoucherSaleToReceipt).
function VoucherPaidOverlay({ result, onNewSale }) {
  const { sale, changeDue, pending } = result;
  const { data: template } = useGetReceiptTemplateQuery();
  const { data: buyerRes } = useGetCustomerQuery(sale.buyer_customer_id, { skip: !sale.buyer_customer_id });
  const buyer = buyerRes?.data?.customer || buyerRes?.data;
  const { printReceipt, printerStatus } = useThermalReceiptPrint();
  const receipt = useMemo(() => mapGiftVoucherSaleToReceipt(sale, template, { changeDue }), [sale, template, changeDue]);
  const order = useMemo(() => ({
    id: sale.id,
    document_kind: 'gift_voucher_sale',
    order_number: sale.number,
    customer_id: sale.buyer_customer_id,
    customer_name: sale.buyer_name,
    grand_total: sale.amount_payable,
    amount_paid: round2(Number(sale.amount_paid || 0) + changeDue),
    change_due: changeDue,
    whatsapp_bill_sent_at: sale.whatsapp_bill_sent_at,
  }), [sale, changeDue]);
  const count = (sale.lines || []).reduce((a, l) => a + Number(l.quantity || 0), 0);

  // Enter = New Sale, P = Print — same keys as the normal success screen.
  useEffect(() => {
    const onKey = (e) => {
      if (e.target.tagName === 'INPUT' || e.target.tagName === 'TEXTAREA') return;
      if (e.key === 'Enter') { e.preventDefault(); onNewSale(); }
      else if (e.key === 'p' || e.key === 'P') { e.preventDefault(); printReceipt(receipt); }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onNewSale, printReceipt, receipt]);

  const plural = count !== 1 ? 's' : '';
  return (
    <SuccessOverlay order={order} receipt={receipt} isReceiptLoading={false} onNewSale={onNewSale}
      onPrint={() => printReceipt(receipt)} printerStatus={printerStatus} customerWhatsappOptIn={buyer?.whatsapp_opt_in ?? null}>
      <Box sx={{
        flexShrink: 0, mx: { xs: 2, sm: 2.5 }, mb: 1.25, display: 'flex', gap: 0.75, alignItems: 'flex-start', px: 1.25, py: 0.9, borderRadius: '8px', fontSize: '0.72rem', lineHeight: 1.45,
        ...(pending ? { bgcolor: '#FFFBEB', border: '1px solid #FDE68A', color: '#92400E' } : { bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', color: '#3730A3' }),
      }}>
        {pending ? <InfoOutlined sx={{ fontSize: 16 }} /> : <MarkEmailRead sx={{ fontSize: 16 }} />}
        <span>
          {pending
            ? <>An admin will generate the {count} voucher{plural} from <b>Voucher generation requests</b>. If rejected, refund it from <b>Past sales</b>.</>
            : <>{count} voucher{plural} issued · codes and PINs go by email {sale.delivery_mode === 'individual' ? 'to each person' : `to ${sale.buyer_email}`}.</>}
        </span>
      </Box>
    </SuccessOverlay>
  );
}

// ── Collect payment ─────────────────────────────────────────────────────────
// Rendered only while open, so its payment rows start empty on every open.
export default function VoucherCheckoutPage({ open, onClose }) {
  if (!open) return null;
  return <VoucherCheckoutContent onClose={onClose} />;
}

function VoucherCheckoutContent({ onClose }) {
  const dispatch = useDispatch();
  const showToast = useToast();
  const sale = useSelector((s) => s.giftVoucherSale);
  const server = sale.server;
  const { data: modesData, isLoading: loadingModes } = useGetPaymentModesQuery();
  const modes = useMemo(() => modesData?.data?.payment_modes || (Array.isArray(modesData?.data) ? modesData.data : []), [modesData]);
  const [pay, { isLoading: isPaying }] = usePayGiftVoucherSaleMutation();
  const [payments, setPayments] = useState([]);
  const [result, setResult] = useState(null);


  const payable = Number(server?.amount_payable || 0);
  const paid = round2(payments.reduce((a, p) => a + (parseFloat(p.amount) || 0), 0));
  const cashRow = payments.find((p) => isCashModeGv(modes.find((m) => m.id === p.payment_mode_id)));
  const cashAmt = cashRow ? parseFloat(cashRow.amount) || 0 : 0;
  const nonCash = round2(paid - cashAmt);
  const over = round2(paid - payable);
  // ₹0 rows are ignored (handlePay drops them), so they never block paying.
  const missingRef = payments.some((p) => parseFloat(p.amount) > 0 && needsReference(modes.find((m) => m.id === p.payment_mode_id)) && !String(p.reference_number || '').trim());
  const decimalsBad = payments.some((p) => p.amount !== '' && !/^\d+(\.\d{1,2})?$/.test(String(p.amount)));
  const cashLimitHit = cashAmt >= CASH_LIMIT;
  const changeDue = over > 0 && cashRow && nonCash <= payable ? over : 0;
  const canPay = payments.length > 0 && !isPaying && !missingRef && !decimalsBad && !cashLimitHit && (over === 0 || changeDue > 0)
    && payments.some((p) => parseFloat(p.amount) > 0) && payments.every((p) => !(parseFloat(p.amount) < 0));

  const toggleMode = (mode) => {
    const i = payments.findIndex((p) => p.payment_mode_id === mode.id);
    if (i >= 0) { setPayments((prev) => prev.filter((_, ix) => ix !== i)); return; }
    const rem = round2(payable - paid);
    setPayments((prev) => [...prev, { payment_mode_id: mode.id, amount: rem > 0 ? rem.toFixed(2) : '', reference_number: '' }]);
  };
  const setRow = (i, k, v) => setPayments((prev) => prev.map((p, ix) => (ix === i ? { ...p, [k]: v } : p)));

  const handlePay = async () => {
    // The API wants tenders equal to the payable; cash change is handed back at the counter.
    const tenders = payments.map((p) => {
      let amount = parseFloat(p.amount) || 0;
      if (changeDue > 0 && p === cashRow) amount = round2(amount - changeDue);
      return { payment_mode_id: p.payment_mode_id, amount, ...(p.reference_number?.trim() ? { reference_number: p.reference_number.trim() } : {}) };
    }).filter((p) => p.amount > 0);
    try {
      const res = await pay({ id: sale.saleId, payments: tenders }).unwrap();
      const d = res?.data;
      setResult({ sale: d.gift_voucher_sale, changeDue, pending: !!d.vouchers_pending_generation });
      showToast(d.vouchers_pending_generation ? 'Payment received — waiting for admin' : 'Voucher sale paid', 'success');
    } catch (err) {
      const msg = err?.data?.error || 'Payment failed';
      if (/not awaiting payment/i.test(msg)) showToast('This sale was already paid — showing it now', 'info');
      else showToast(msg, 'error');
    }
  };

  const topBar = (
    <Box sx={{ bgcolor: '#fff', borderBottom: '1px solid #E2E8F0', px: { xs: 1.5, sm: 2.5 }, py: { xs: 1.1, sm: 1.4 }, display: 'flex', alignItems: 'center', gap: 1.25, flexShrink: 0, boxShadow: '0 1px 3px rgba(0,0,0,0.04)' }}>
      {!result && (
        <IconButton size="small" onClick={onClose} disabled={isPaying} sx={{ color: '#64748B', bgcolor: '#F8FAFC', border: '1px solid #E2E8F0', borderRadius: '8px', p: 0.55 }} aria-label="Back">
          <ArrowBack sx={{ fontSize: 18 }} />
        </IconButton>
      )}
      <Box sx={{ flexGrow: 1, minWidth: 0 }}>
        <Typography noWrap sx={{ fontWeight: 800, fontSize: { xs: '0.9rem', sm: '1rem' }, color: '#1E293B', lineHeight: 1.2 }}>Collect payment</Typography>
        <Typography noWrap sx={{ fontSize: '0.67rem', color: '#94A3B8', fontWeight: 500 }}>Gift voucher sale</Typography>
      </Box>
      {sale.buyer && (
        <Chip icon={<Person sx={{ fontSize: '13px !important', color: '#6366F1 !important' }} />} label={sale.buyer.name} size="small"
          sx={{ height: 24, fontSize: '0.72rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5', flexShrink: 0, maxWidth: { xs: 120, sm: 200 }, '& .MuiChip-label': { overflow: 'hidden', textOverflow: 'ellipsis' } }} />
      )}
    </Box>
  );

  const items = (server?.lines || []).map((l) => (
    <Box key={l.id} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1.25, py: 1.25, borderBottom: '1px solid #F1F5F9', '&:last-child': { borderBottom: 'none' } }}>
      <Box sx={{ minWidth: 0 }}>
        <Typography noWrap sx={{ fontSize: '0.82rem', fontWeight: 700 }}>{l.gift_voucher_type_name}</Typography>
        <Typography noWrap sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 0.25 }}>
          {inr(l.face_value)} × {l.quantity}{l.recipients?.length ? ` · ${l.recipients.map((r) => (r.name || r.email).split(' ')[0]).join(', ')}` : ''}
        </Typography>
      </Box>
      <Typography sx={{ fontSize: '0.85rem', fontWeight: 800, whiteSpace: 'nowrap' }}>{inr2(l.line_payable)}</Typography>
    </Box>
  ));

  const billing = (
    <Box>
      {[['Face value', inr2(server?.face_value_total)],
        ...(Number(server?.discount_total) > 0 ? [[`Discount${server.discount_reason ? ` (${server.discount_reason})` : ''}`, `−${inr2(server.discount_total)}`, '#16A34A']] : []),
        ['Tax', 'Not applicable', '#94A3B8']].map(([k, v, c]) => (
        <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', gap: 1, fontSize: '0.78rem', py: 0.4, color: '#64748B' }}>
          <span>{k}</span><Box component="b" sx={{ color: c || '#1E293B', fontWeight: 700 }}>{v}</Box>
        </Box>
      ))}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 0.75, pt: 1, borderTop: '1px dashed #E2E8F0' }}>
        <Typography sx={{ fontWeight: 900, fontSize: '0.8rem' }}>AMOUNT PAYABLE</Typography>
        <Typography sx={{ fontWeight: 900, fontSize: '1.35rem', color: '#4F46E5' }}>{inr2(payable)}</Typography>
      </Box>
      <Typography sx={{ fontSize: '0.66rem', color: '#94A3B8', mt: 1, display: 'flex', gap: 0.5, alignItems: 'flex-start' }}>
        <ReceiptLong sx={{ fontSize: 13, mt: '1px' }} />
        Receipt: “Gift Voucher Sale Receipt – Not a Tax Invoice”. Delivery: {sale.delivery === 'individual' ? 'each person gets their own email' : `all codes to ${sale.buyerEmail}`}.
      </Typography>
    </Box>
  );

  const paymentPanel = (
    <Box>
      <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.07em', mb: 1.25 }}>PAYMENT METHOD</Typography>
      {loadingModes ? (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, py: 1 }}><CircularProgress size={14} sx={{ color: '#94A3B8' }} /><Typography sx={{ fontSize: '0.75rem', color: '#94A3B8' }}>Loading…</Typography></Box>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: 0.75 }}>
          {modes.map((mode) => {
            const blocked = isBlockedForVoucherSale(mode);
            const selected = payments.some((p) => p.payment_mode_id === mode.id);
            return (
              <Box key={mode.id} role="checkbox" aria-checked={selected} aria-disabled={blocked} tabIndex={0}
                onClick={() => (blocked ? showToast(`${mode.name} cannot be used to buy gift vouchers`, 'error') : toggleMode(mode))}
                onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && !blocked) { e.preventDefault(); toggleMode(mode); } }}
                sx={{
                  display: 'flex', alignItems: 'center', gap: 0.6, pl: 0.9, pr: 0.5, py: 0.7, borderRadius: '8px', cursor: blocked ? 'not-allowed' : 'pointer', userSelect: 'none',
                  border: '1.5px solid', borderColor: blocked ? '#E2E8F0' : selected ? '#4F46E5' : '#E2E8F0', bgcolor: blocked ? '#F8FAFC' : selected ? '#EEF2FF' : '#F8FAFC',
                  boxShadow: selected ? '0 0 0 3px rgba(79,70,229,0.1)' : 'none', opacity: blocked ? 0.55 : 1,
                  '&:hover': blocked ? {} : { borderColor: '#818CF8', bgcolor: '#F5F3FF' }, '&:focus-visible': { outline: '2px solid #4F46E5', outlineOffset: '2px' },
                }}>
                {modeIcon(mode, blocked ? '#CBD5E1' : selected ? '#4F46E5' : '#94A3B8')}
                <Typography sx={{ fontSize: { xs: '0.6rem', md: '0.72rem' }, fontWeight: 700, lineHeight: 1.25, color: blocked ? '#94A3B8' : selected ? '#4F46E5' : '#64748B', wordBreak: 'break-word' }}>{mode.name}</Typography>
              </Box>
            );
          })}
        </Box>
      )}
      <Typography sx={{ fontSize: '0.68rem', color: '#94A3B8', mt: 1 }}>Store credit and gift vouchers can’t be used to buy vouchers.</Typography>
      {payments.length > 0 ? (
        <Stack spacing={0.75} sx={{ mt: 1.75 }}>
          <Divider sx={{ mb: 0.75 }} />
          {payments.map((p, i) => {
            const mode = modes.find((m) => m.id === p.payment_mode_id);
            const refNeeded = needsReference(mode);
            return (
              <React.Fragment key={p.payment_mode_id}>
                <Box sx={{ display: 'flex', alignItems: 'center', height: 46, bgcolor: '#F8FAFC', border: '1.5px solid #E2E8F0', borderRadius: '10px', overflow: 'hidden' }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.6, px: 1.25, width: 128, flexShrink: 0, borderRight: '1px solid #E2E8F0', height: '100%', bgcolor: '#F1F5F9' }}>
                    {modeIcon(mode, '#4F46E5')}
                    <Typography sx={{ fontSize: '0.7rem', fontWeight: 700, color: '#4F46E5', lineHeight: 1.25, textAlign: 'center', wordBreak: 'break-word' }}>{mode?.name}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1, px: 1.25, gap: 0.25, minWidth: 0 }}>
                    <CurrencyRupee sx={{ fontSize: 15, color: '#64748B', flexShrink: 0 }} />
                    <TextField variant="standard" placeholder="0.00" value={p.amount} onChange={(e) => setRow(i, 'amount', e.target.value.replace(/[^\d.]/g, ''))}
                      InputProps={{ disableUnderline: true }} inputProps={{ inputMode: 'decimal', 'aria-label': `${mode?.name} amount`, style: { fontSize: '1rem', fontWeight: 800, padding: 0 } }} sx={{ flexGrow: 1 }} />
                  </Box>
                  <IconButton size="small" onClick={() => setPayments((prev) => prev.filter((_, ix) => ix !== i))} aria-label={`Remove ${mode?.name}`}
                    sx={{ mr: 0.75, p: 0.4, color: '#CBD5E1', borderRadius: '6px', '&:hover': { color: '#EF4444', bgcolor: '#FEF2F2' } }}>
                    <Delete sx={{ fontSize: 15 }} />
                  </IconButton>
                </Box>
                {refNeeded && (
                  <TextField size="small" value={p.reference_number} onChange={(e) => setRow(i, 'reference_number', e.target.value)} placeholder="Reference no. (UTR) — required"
                    error={!String(p.reference_number || '').trim()} inputProps={{ 'aria-label': `${mode?.name} reference number` }}
                    sx={{ '& .MuiOutlinedInput-root': { height: 36, borderRadius: '8px', fontSize: '0.8rem', bgcolor: '#FFF' } }} />
                )}
              </React.Fragment>
            );
          })}
        </Stack>
      ) : (
        <Typography sx={{ fontSize: '0.7rem', color: '#CBD5E1', mt: 1, fontStyle: 'italic' }}>Tap a method above — tap more than one to split</Typography>
      )}
      {payments.length > 0 && (over < 0 ? <StatusPill tone="bad" label="REMAINING" value={inr2(-over)} />
        : changeDue > 0 ? <StatusPill tone="ok" label="CHANGE DUE" value={inr2(changeDue)} />
          : over > 0 ? <StatusPill tone="bad" label="MORE THAN PAYABLE" value={inr2(over)} />
            : <StatusPill tone="ok" label="✓ FULLY PAID" value="" />)}
      {cashLimitHit ? (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.25, px: 1.4, py: 1, borderRadius: '8px', bgcolor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '0.72rem' }}>
          <GppBad sx={{ fontSize: 17 }} /><span>Cash of ₹2,00,000 or more from one buyer in a day is not allowed (Income Tax Act s.269ST). Use UPI, card or bank transfer.</span>
        </Box>
      ) : over > 0 && !changeDue ? (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.25, px: 1.4, py: 1, borderRadius: '8px', bgcolor: '#FEF2F2', border: '1px solid #FECACA', color: '#DC2626', fontSize: '0.72rem' }}>
          <ErrorOutline sx={{ fontSize: 17 }} /><span>Payments must equal the amount payable. Only cash can be more (change is given back).</span>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', gap: 1, mt: 1.5, px: 1.4, py: 1, borderRadius: '8px', bgcolor: '#EEF2FF', border: '1px solid #C7D2FE', color: '#3730A3', fontSize: '0.72rem' }}>
          <InfoOutlined sx={{ fontSize: 17 }} /><span>Cash from one buyer across a day must stay under ₹2,00,000 (s.269ST).</span>
        </Box>
      )}
    </Box>
  );

  const actions = (compact) => (
    <Stack direction="row" spacing={1.25}>
      <Button variant="outlined" onClick={onClose} disabled={isPaying}
        sx={{ flex: 1, py: compact ? 0.9 : 1.1, borderRadius: '9px', fontWeight: 700, textTransform: 'none', borderColor: '#E2E8F0', color: '#64748B' }}>
        Cancel
      </Button>
      <Button variant="contained" disableElevation onClick={handlePay} disabled={!canPay}
        sx={{ flex: 2, py: compact ? 0.9 : 1.1, borderRadius: '9px', fontWeight: 800, fontSize: compact ? '0.88rem' : '0.92rem', textTransform: 'none', bgcolor: '#4F46E5', '&:hover': { bgcolor: '#4338CA' }, '&.Mui-disabled': { bgcolor: '#E2E8F0', color: '#94A3B8' } }}>
        {isPaying ? <CircularProgress size={16} sx={{ color: '#fff' }} /> : `Pay ${inr2(payable)}`}
      </Button>
    </Stack>
  );

  const count = (server?.lines || []).reduce((a, l) => a + l.quantity, 0);

  return (
    <Box sx={{ position: 'fixed', inset: 0, zIndex: 1200, height: '100dvh', bgcolor: '#F1F5F9', display: 'flex', flexDirection: 'column' }}>
      {topBar}
      {result && <VoucherPaidOverlay result={result} onNewSale={() => { dispatch(resetGvSale()); onClose(); }} />}
      <Box sx={{ display: { xs: 'none', md: 'flex' }, flexGrow: 1, overflow: 'hidden', gap: 2, p: 2 }}>
        <Box sx={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Paper variant="outlined" sx={{ borderRadius: '12px', flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
            <Box sx={{ px: 2.5, pt: 2, pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
              <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.07em' }}>VOUCHERS</Typography>
              <Chip label={`${count} voucher${count !== 1 ? 's' : ''}`} size="small" sx={{ height: 18, fontSize: '0.63rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5' }} />
            </Box>
            <Divider />
            <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2.5 }}>{items}</Box>
          </Paper>
          <Paper variant="outlined" sx={{ borderRadius: '12px', px: 2.5, py: 1.5, flexShrink: 0 }}>{billing}</Paper>
        </Box>
        <Paper variant="outlined" sx={{ flex: 1, minWidth: 0, minHeight: 0, borderRadius: '12px', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
          <Box sx={{ flexGrow: 1, overflowY: 'auto', px: 2.5, pt: 2.25, pb: 1 }}>{paymentPanel}</Box>
          <Box sx={{ px: 2, py: 2, borderTop: '1px solid #F1F5F9', flexShrink: 0 }}>{actions(false)}</Box>
        </Paper>
      </Box>
      <Box sx={{ display: { xs: 'flex', md: 'none' }, flexDirection: 'column', flexGrow: 1, minHeight: 0, overflow: 'hidden' }}>
        <Box sx={{ flexGrow: 1, minHeight: 0, overflowY: 'auto', px: 1.5, py: 1.5 }}>
          <Stack spacing={1.5}>
            <Paper variant="outlined" sx={{ borderRadius: '10px', px: 2, pt: 1.5, pb: 0.75 }}>
              <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                <Typography sx={{ fontSize: '0.63rem', fontWeight: 900, color: '#94A3B8', letterSpacing: '0.07em' }}>VOUCHERS</Typography>
                <Chip label={`${count}`} size="small" sx={{ height: 18, fontSize: '0.63rem', fontWeight: 700, bgcolor: '#EEF2FF', color: '#4F46E5' }} />
              </Stack>
              {items}
            </Paper>
            <Paper variant="outlined" sx={{ borderRadius: '10px', px: 2, py: 1.5 }}>{paymentPanel}</Paper>
            <Paper variant="outlined" sx={{ borderRadius: '10px', px: 2, py: 1.5 }}>{billing}</Paper>
          </Stack>
        </Box>
        <Box sx={{ px: 1.5, py: 1.25, bgcolor: '#fff', borderTop: '1px solid #E2E8F0', flexShrink: 0 }}>{actions(true)}</Box>
      </Box>
    </Box>
  );
}
